/**
 * Amazon deliveries need two numbers, not one.
 *
 * Amazon warehouses want a reference ID and a shipment ID, and both are
 * mandatory. They travel as first-class fields to the server, which owns
 * packing them into the single carrier-facing reference number.
 */

import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import { isAmazonDistributionWarehouseBrand } from "@freightsimple/shared/Helpers/isAmazonDistributionWarehouseBrand";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import { useEffect, useState } from "react";

interface BookShipmentAmazonReferenceInput {
  context: LocationContext;
  location: PreQuoteLocation;
  originalAmazonReferenceId: string | undefined;
  originalAmazonShipmentId: string | undefined;
}

export function useBookShipmentAmazonReference(
  input: BookShipmentAmazonReferenceInput,
) {
  const {
    context,
    location,
    originalAmazonReferenceId,
    originalAmazonShipmentId,
  } = input;

  // Mirrors the server-side gate exactly: locationType AND brand. A location
  // whose type was switched away from DistributionWarehouse can keep a stale
  // "amazon" brand, and sending ids for it would be a 400.
  const isAmazonDelivery =
    context === LocationContext.Delivery &&
    location.locationType === LocationType.DistributionWarehouse &&
    isAmazonDistributionWarehouseBrand(location.distributionWarehouseBrand);

  const [amazonReferenceId, setAmazonReferenceId] = useState(
    originalAmazonReferenceId ?? "",
  );
  const [amazonShipmentId, setAmazonShipmentId] = useState(
    originalAmazonShipmentId ?? "",
  );

  // The shipment can arrive after mount, so re-seed when the stored ids show
  // up. Only ever fills in a value - it never wipes what the user typed.
  useEffect(
    function () {
      if (isAmazonDelivery) {
        if (originalAmazonReferenceId) {
          setAmazonReferenceId(originalAmazonReferenceId);
        }
        if (originalAmazonShipmentId) {
          setAmazonShipmentId(originalAmazonShipmentId);
        }
      }
    },
    [isAmazonDelivery, originalAmazonReferenceId, originalAmazonShipmentId],
  );

  function amazonReferenceIdError(): string | undefined {
    if (isAmazonDelivery) {
      if (!amazonReferenceId || amazonReferenceId.trim() === "") {
        return "Amazon Reference ID is required";
      }
      if (amazonReferenceId.includes(",")) {
        return "Amazon Reference ID must not contain commas";
      }
    }
    return undefined;
  }

  function amazonShipmentIdError(): string | undefined {
    if (isAmazonDelivery) {
      if (!amazonShipmentId || amazonShipmentId.trim() === "") {
        return "Amazon Shipment ID is required";
      }
      if (amazonShipmentId.includes(",")) {
        return "Amazon Shipment ID must not contain commas";
      }
    }
    return undefined;
  }

  return {
    isAmazonDelivery,
    amazonReferenceId,
    setAmazonReferenceId,
    amazonShipmentId,
    setAmazonShipmentId,
    amazonReferenceIdError,
    amazonShipmentIdError,
  };
}
